import useVersionManagement from './useVersionManagement';
import { parseDevelopmentGuidelines, parseMarkdownIntoSections } from '../utils/markdownSectionParser';

/**
 * Custom hook to manage API Guidelines versions
 * Stores and tracks different versions of API guidelines from regenerations
 */
const useAPIGuidelinesVersions = (conversation, isStreaming, streamingContent) => {
  // Configuration for API guidelines version management
  const apiGuidelinesConfig = {
    // Determine if a message contains API guidelines
    isRelevantMessage: (msg) => {
      const content = msg.content || '';
      // Check for API guidelines specific patterns
      return content.includes('## API Design') ||
             content.includes('## Authentication') ||
             content.includes('## Error Handling') ||
             content.includes('### ') || // Has section markers
             content.includes('**Why This Matters:**');
    },

    // Parse API guidelines from content
    parseContent: (content, msg) => {
      if (msg?.parsed_content?.sections?.length) {
        return msg.parsed_content.sections;
      }
      // Debug: log first 1000 chars of content
      console.log('🔍 useAPIGuidelinesVersions: Parsing content (first 1000 chars):', content?.substring(0, 1000));

      // Try development guidelines parser first (H2 categories + H3 sections)
      let sections = parseDevelopmentGuidelines(content);
      console.log('🔍 After parseDevelopmentGuidelines:', sections.length, 'sections, sample:', sections.slice(0, 2));

      // Fallback to simple H2 parsing if no H3 sections found
      if (!sections || sections.length === 0) {
        console.log('⚠️ Falling back to parseMarkdownIntoSections');
        sections = parseMarkdownIntoSections(content);
        console.log('🔍 After parseMarkdownIntoSections:', sections.length, 'sections, sample:', sections.slice(0, 2));
      }

      return sections;
    },

    // Extract version information from a message
    extractVersions: (msg, parsedSections, versionNumber, isPreview = false) => {
      console.log('📌 extractVersions called:', {
        versionNumber,
        sectionsCount: parsedSections?.length || 0,
        sampleSection: parsedSections?.[0],
        hasCategories: parsedSections?.some(s => s.category != null),
        willReparse: !parsedSections?.some(s => s.category != null)
      });

      // FORCE RE-PARSE if sections don't have categories (fixes legacy data)
      let finalSections = parsedSections;
      if (parsedSections && parsedSections.length > 0 && !parsedSections.some(s => s.category != null)) {
        console.log('⚠️ Sections missing categories - re-parsing from content');
        const reparsed = parseDevelopmentGuidelines(msg.content);
        if (reparsed && reparsed.length > 0) {
          finalSections = reparsed;
          console.log('✅ Re-parsed successfully:', finalSections.length, 'sections with',
            [...new Set(finalSections.map(s => s.category).filter(Boolean))].length, 'categories');
        }
      }

      return {
        id: msg._id || msg.id || `${msg.createdAt}-${versionNumber}`,
        timestamp: msg.createdAt || new Date().toISOString(),
        sections: finalSections,
        content: msg.content,
        versionNumber: versionNumber,
        isPreview: isPreview,
        label: isPreview ? 'Generating...' : undefined
      };
    },

    conversation,
    isStreaming,
    streamingContent
  };

  // Use the generic version management hook
  const versionData = useVersionManagement(apiGuidelinesConfig);

  // Map the generic version data to API guidelines-specific structure
  return {
    versions: versionData.versions,
    currentVersion: versionData.currentVersion ? {
      ...versionData.currentVersion,
      sections: versionData.currentVersion.parsedContent || versionData.currentVersion.sections
    } : null,
    currentVersionIndex: versionData.currentVersionIndex,
    setCurrentVersionIndex: versionData.setCurrentVersionIndex,
    hasVersions: versionData.hasVersions,
    isLatestVersion: versionData.isLatestVersion,
    addVersion: versionData.addVersion,
    updateCurrentVersion: versionData.updateCurrentVersion
  };
};

export default useAPIGuidelinesVersions;
